import axios from 'axios';

// Determine if we're on cloud website or localhost
const isBrowser = typeof window !== 'undefined';
const hostname = isBrowser ? window.location.hostname : 'localhost';
const currentOrigin = isBrowser ? String(window.location.origin || '').replace(/\/$/, '') : '';
const currentPort = isBrowser ? window.location.port : '';
const isLocalhost = hostname === 'localhost' || 
                    hostname === '127.0.0.1' || 
                    hostname.startsWith('192.168.') ||
                    hostname.startsWith('10.') ||
                    hostname.startsWith('172.16.') ||
                    hostname.startsWith('172.17.') ||
                    hostname.startsWith('172.18.') ||
                    hostname.startsWith('172.19.') ||
                    hostname.startsWith('172.20.') ||
                    hostname.startsWith('172.21.') ||
                    hostname.startsWith('172.22.') ||
                    hostname.startsWith('172.23.') ||
                    hostname.startsWith('172.24.') ||
                    hostname.startsWith('172.25.') ||
                    hostname.startsWith('172.26.') ||
                    hostname.startsWith('172.27.') ||
                    hostname.startsWith('172.28.') ||
                    hostname.startsWith('172.29.') ||
                    hostname.startsWith('172.30.') ||
                    hostname.startsWith('172.31.');

// Set cloud backend URL.
// If REACT_APP_API_URL is missing OR points to localhost, force Render cloud URL.
// This prevents accidental localhost:5019 calls in hybrid/local-hub usage.
const DEFAULT_CLOUD_URL = 'https://elydine-multitenant.onrender.com';
const ENV_API_URL = process.env.REACT_APP_API_URL;
const ENV_POINTS_LOCALHOST = Boolean(
  ENV_API_URL && /localhost|127\.0\.0\.1/i.test(ENV_API_URL)
);

const CLOUD_BASE_URL = (!ENV_API_URL || ENV_POINTS_LOCALHOST)
  ? DEFAULT_CLOUD_URL
  : ENV_API_URL;
// When the app is served by local-hub (including tablet via PC IP), force same-origin API base.
// This avoids cross-origin localhost calls from devices where localhost points to the device itself.
const RUNTIME_LOCAL_BASE_URL = (isBrowser && currentPort === '9102' && currentOrigin)
  ? currentOrigin
  : null;
const LOCAL_BASE_URL = RUNTIME_LOCAL_BASE_URL || process.env.REACT_APP_LOCAL_HUB_URL || 'http://localhost:9102';
const HEALTH_TIMEOUT_MS = 1500;
const BASE_URL_CACHE_MS = 3000;

let cachedBaseUrl = CLOUD_BASE_URL;
let lastBaseCheck = 0;
let inflightBaseCheck = null;

const healthClient = axios.create({ timeout: HEALTH_TIMEOUT_MS });

async function checkCloudHealth() {
  try {
    await healthClient.get(`${CLOUD_BASE_URL}/api/health`);
    return true;
  } catch (error) {
    return false;
  }
}

async function checkLocalHubHealth() {
  try {
    await healthClient.get(`${LOCAL_BASE_URL}/health`);
    return true;
  } catch (error) {
    return false;
  }
}

export async function resolveBaseUrl() {
  // If loaded from local-hub UI, use same origin directly to avoid CORS/IP issues on tablets.
  if (RUNTIME_LOCAL_BASE_URL) {
    cachedBaseUrl = RUNTIME_LOCAL_BASE_URL;
    return cachedBaseUrl;
  }

  // Always PREFER cloud. Local hub is a fallback only when cloud is unreachable.

  // Short‑circuit cache if recent
  const now = Date.now();
  if (now - lastBaseCheck < BASE_URL_CACHE_MS && cachedBaseUrl) {
    return cachedBaseUrl;
  }

  if (inflightBaseCheck) {
    return inflightBaseCheck;
  }

  inflightBaseCheck = (async () => {
    lastBaseCheck = Date.now();

    // 1) If browser reports offline, skip cloud and try local hub
    const offline =
      isBrowser &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false;

    if (!offline) {
      // 2) Try cloud first
      const cloudOk = await checkCloudHealth();
      if (cloudOk) {
        cachedBaseUrl = CLOUD_BASE_URL;
        inflightBaseCheck = null;
        return cachedBaseUrl;
      }
    }

    // 3) Cloud not reachable or browser offline → try local hub
    const localOk = await checkLocalHubHealth();
    if (localOk) {
      cachedBaseUrl = LOCAL_BASE_URL;
    } else {
      // 4) As a last resort, fall back to cloud base URL
      cachedBaseUrl = CLOUD_BASE_URL;
    }

    inflightBaseCheck = null;
    return cachedBaseUrl;
  })();

  return inflightBaseCheck;
}

export function getCachedBaseUrl() {
  return cachedBaseUrl;
}

const api = axios.create({
  baseURL: CLOUD_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to conditionally add auth token
api.interceptors.request.use(
  async (config) => {
    const baseUrl = await resolveBaseUrl();
    config.baseURL = baseUrl;

    // Don't add auth token for public routes
    // Only GET /api/tables/:id (single table by ID) is public for QR menu
    // All other table routes (GET /api/tables, PATCH /api/tables/layout/bulk, etc.) require auth
    const urlPath = config.url?.replace(config.baseURL || '', '') || config.url || '';
    // Check if it's GET /api/tables/:id (exactly one segment after /api/tables/)
    const tableIdMatch = urlPath.match(/^\/api\/tables\/([^/]+)$/);
    const isPublicTableRoute = config.method === 'get' && tableIdMatch !== null;
    
    // Check if it's a public feedback route (GET /api/feedback/token/:token or POST /api/feedback/token/:token/submit)
    const feedbackTokenMatch = urlPath.match(/^\/api\/feedback\/token\/([^/]+)(\/submit)?$/);
    const isPublicFeedbackRoute = feedbackTokenMatch !== null;
    
    // Only GET /api/menu with tableId is public (QR menu display)
    // Otherwise keep auth for restaurant dashboard/admin usage.
    const hasPublicMenuParam = Boolean(
      config.params?.tableId ||
      config.params?.tenantSlug ||
      (typeof config.url === 'string' && config.url.includes('tableId='))
    );
    const isPublicMenuRoute = config.method === 'get' &&
      config.url?.includes('/api/menu') &&
      (hasPublicMenuParam || (typeof config.url === 'string' && config.url.includes('tenantSlug=')));
    
    const isPublicRoute = isPublicMenuRoute || 
                         isPublicTableRoute ||
                         config.url?.includes('/api/public/tenant/modules') ||
                         config.url?.includes('/api/orders/public') ||
                         config.url?.includes('/api/guests/restaurant/public') ||
                         config.url?.includes('/api/waiter-calls/public') ||
                         isPublicFeedbackRoute;
    
    if (isPublicRoute) {
      // Remove auth header for public routes
      delete config.headers['Authorization'];
    } else {
      // Add auth token for protected routes
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const ownerTenantId = localStorage.getItem('ownerTenantId');
    if (ownerTenantId) {
      config.headers['X-Tenant-Id'] = ownerTenantId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const currentPath = window.location.pathname;
    const isPublicRoute = currentPath.startsWith('/menu/');
    
    // Only handle authentication errors (401) - not validation or server errors
    if (status === 401) {
      // Check error message to confirm it's a real auth error
      const errorMessage = error.response?.data?.error || '';
      const isAuthError = errorMessage.toLowerCase().includes('token') ||
                         errorMessage.toLowerCase().includes('authentication') ||
                         errorMessage.toLowerCase().includes('unauthorized') ||
                         errorMessage.toLowerCase().includes('access token required');
      
      // Only logout and redirect for real authentication errors
      // Skip for public routes or login pages
      if (isAuthError && !isPublicRoute && !currentPath.includes('/login')) {
        localStorage.removeItem('token');
        
        // Determine which login page to redirect to based on current path
        if (currentPath.includes('/restaurant')) {
          window.location.href = '/login/restaurant';
        } else if (currentPath.includes('/staff')) {
          window.location.href = '/login/staff';
        } else {
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    }
    
    // For 403 (Forbidden), don't logout - just reject the promise
    // The component will handle showing appropriate error messages
    if (status === 403) {
      // Don't logout for permission errors - just let the component handle it
      return Promise.reject(error);
    }
    
    // For all other errors, just reject and let components handle them
    return Promise.reject(error);
  }
);

export default api;



